import React, { useState, useEffect } from 'react';
import { Popover, List, Button, Typography, Space, Spin, Empty, Badge } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  BellOutlined,
  FileTextOutlined,
  SearchOutlined,
  BulbOutlined,
  DollarOutlined,
  TeamOutlined,
  SendOutlined,
  MessageOutlined,
  NotificationOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import type { Notification } from '../../types/notification';
import { COLORS } from '../../constants/colors';
import { useNotificationStore } from '../../stores/notificationStore';

const { Text, Paragraph } = Typography;

// 通知类型图标映射
const notificationTypeIcons: Record<string, React.ReactNode> = {
  proposal_created: <FileTextOutlined />,
  requirement_analyzed: <SearchOutlined />,
  insight_generated: <BulbOutlined />,
  pricing_generated: <DollarOutlined />,
  references_ready: <TeamOutlined />,
  campaign_sent: <SendOutlined />,
  campaign_replied: <MessageOutlined />,
  system_announcement: <NotificationOutlined />,
  default: <BellOutlined />,
};

// 获取相对时间
const getRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) return '刚刚';
  if (diffMinutes < 60) return `${diffMinutes}分钟前`;
  if (diffHours < 24) return `${diffHours}小时前`;
  if (diffDays < 7) return `${diffDays}天前`;
  return date.toLocaleDateString('zh-CN');
};

interface NotificationPopoverProps {
  children: React.ReactNode;
}

export const NotificationPopover: React.FC<NotificationPopoverProps> = ({ children }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { notifications, isLoading, fetchNotifications, markAsRead, markAllAsRead } =
    useNotificationStore();

  // 加载最近通知
  useEffect(() => {
    if (open && notifications.length === 0) {
      fetchNotifications(1, 10);
    }
  }, [open, notifications.length, fetchNotifications]);

  // 点击通知
  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }
    setOpen(false);
    if (notification.action_url) {
      navigate(notification.action_url);
    }
  };

  // 全部标记已读
  const handleMarkAllRead = async () => {
    await markAllAsRead();
  };

  // 查看全部
  const handleViewAll = () => {
    setOpen(false);
    navigate('/notifications');
  };

  // 通知项内容
  const NotificationItem: React.FC<{ notification: Notification }> = ({ notification }) => (
    <List.Item
      style={{
        padding: '12px 0',
        cursor: 'pointer',
        backgroundColor: notification.is_read ? 'transparent' : COLORS.bgLight,
        borderBottom: `1px solid ${COLORS.gray200}`,
        transition: 'background-color 0.2s',
      }}
      onClick={() => handleNotificationClick(notification)}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%', gap: 12 }}>
        {/* 图标 */}
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            backgroundColor: COLORS.bgPrimaryLight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            color: COLORS.primary,
          }}
        >
          {notificationTypeIcons[notification.type] || notificationTypeIcons.default}
        </div>

        {/* 内容 */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Text
              strong
              style={{
                fontSize: 14,
                color: notification.is_read ? COLORS.gray500 : COLORS.gray600,
              }}
            >
              {notification.title}
            </Text>
            {!notification.is_read && (
              <Badge dot style={{ backgroundColor: COLORS.primary }} />
            )}
          </div>
          <Paragraph
            ellipsis={{ rows: 2 }}
            style={{ margin: 0, fontSize: 12, color: COLORS.textSecondary }}
          >
            {notification.content}
          </Paragraph>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {getRelativeTime(notification.created_at)}
          </Text>
        </div>
      </div>
    </List.Item>
  );

  // Popover 内容
  const content = (
    <div style={{ width: 360, maxHeight: 480, display: 'flex', flexDirection: 'column' }}>
      {/* 标题 */}
      <div
        style={{
          padding: '12px 16px',
          borderBottom: `1px solid ${COLORS.gray200}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Text strong style={{ fontSize: 16 }}>
          通知
        </Text>
        <Button
          type="link"
          size="small"
          icon={<CheckOutlined />}
          onClick={handleMarkAllRead}
          style={{ padding: 0, height: 'auto' }}
        >
          全部已读
        </Button>
      </div>

      {/* 通知列表 */}
      <div style={{ flex: 1, overflow: 'auto', padding: '0 16px' }}>
        <Spin spinning={isLoading}>
          {notifications.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="暂无通知"
              style={{ padding: '40px 0' }}
            />
          ) : (
            <List
              dataSource={notifications.slice(0, 10)}
              renderItem={(item) => <NotificationItem notification={item} />}
              style={{ padding: 0 }}
            />
          )}
        </Spin>
      </div>

      {/* 底部操作 */}
      <div
        style={{
          padding: '12px 16px',
          borderTop: `1px solid ${COLORS.gray200}`,
          textAlign: 'center',
        }}
      >
        <Button type="link" onClick={handleViewAll}>
          查看全部通知
        </Button>
      </div>
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      content={content}
      trigger="click"
      placement="bottomRight"
      arrow={false}
      overlayStyle={{ paddingTop: 0 }}
    >
      {children}
    </Popover>
  );
};
